import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadFile, deleteFile, getFilePathFromUrl } from '@/lib/storage';
import { fileTypeIcons, fileTypeColors, fileTypeLabels } from '@/mocks/files';

interface StoredFile {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  created_at: string;
  storage_path?: string;
}

interface ProjectFilesProps {
  projectId: string;
}

export default function ProjectFiles({ projectId }: ProjectFilesProps) {
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkData, setLinkData] = useState({ name: '', url: '' });
  const [addingLink, setAddingLink] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteData, setNoteData] = useState({ title: '', content: '' });
  const [addingNote, setAddingNote] = useState(false);
  const [showEditNote, setShowEditNote] = useState(false);
  const [editingNote, setEditingNote] = useState<StoredFile | null>(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [isNoteLoading, setIsNoteLoading] = useState(false);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // ✅ FIX 1: Sayfa açılınca dosyaları veritabanından çek
  useEffect(() => {
    async function fetchFiles() {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('files')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Dosyalar yüklenirken bir hata oluştu');
        setLoading(false);
        return;
      }

      setFiles(data || []);
      setLoading(false);
    }

    fetchFiles();
  }, [projectId]);

  const handleUpload = async (fileList: FileList) => {
    setUploading(true);
    setError('');

    for (const file of Array.from(fileList)) {
      // Storage'a yükle
      const { url, error: uploadError } = await uploadFile(file, projectId, 'files');

      if (uploadError || !url) {
        const msg = uploadError?.message || 'Bilinmeyen hata';
        if (msg.includes('bucket') || msg.includes('Bucket')) {
          setError('Storage bucket bulunamadı. Lütfen admin ile iletişime geçin.');
        } else if (msg.includes('auth') || msg.includes('Auth')) {
          setError('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        } else {
          setError(`"${file.name}" yüklenirken hata oluştu: ${msg}`);
        }
        continue;
      }

      // ✅ FIX 2: Veritabanına kaydet
      const { data: inserted, error: dbError } = await supabase
        .from('files')
        .insert({
          project_id: projectId,
          file_name: file.name,
          file_url: url,
          file_type: file.type || 'application/octet-stream',
        })
        .select()
        .single();

      if (dbError) {
        // DB hatası olursa storage'dan geri sil (orphan dosya bırakma)
        const path = getFilePathFromUrl(url);
        if (path) await deleteFile(path);
        setError(`"${file.name}" kaydedilirken hata oluştu. Lütfen tekrar deneyin.`);
        continue;
      }

      if (inserted) {
        setFiles((prev) => [inserted, ...prev]);
      }
    }

    setUploading(false);
    setShowUpload(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    handleUpload(selectedFiles);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };
  
  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkData.name.trim() || !linkData.url.trim()) return;
    
    setAddingLink(true);
    setError('');
    
    let finalUrl = linkData.url.trim();
    if (!finalUrl.startsWith('http')) {
      finalUrl = `https://${finalUrl}`;
    }

    const { data: inserted, error: dbError } = await supabase
      .from('files')
      .insert({
        project_id: projectId,
        file_name: linkData.name.trim(),
        file_url: finalUrl,
        file_type: 'url',
      })
      .select()
      .single();

    if (dbError) {
      setError('Bağlantı kaydedilirken bir hata oluştu');
    } else if (inserted) {
      setFiles((prev) => [inserted, ...prev]);
      setShowAddLink(false);
      setLinkData({ name: '', url: '' });
    }
    
    setAddingLink(false);
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteData.title.trim() || !noteData.content.trim()) return;
    
    setAddingNote(true);
    setError('');
    
    const blob = new Blob([noteData.content], { type: 'text/plain;charset=utf-8' });
    const fileName = `${noteData.title.trim()}.txt`;
    const file = new File([blob], fileName, { type: 'text/plain' });

    // Storage'a yükle
    const { url, error: uploadError } = await uploadFile(file, projectId, 'files');

    if (uploadError || !url) {
      setError(`Not yüklenirken hata oluştu: ${uploadError?.message || 'Bilinmeyen hata'}`);
      setAddingNote(false);
      return;
    }

    const { data: inserted, error: dbError } = await supabase
      .from('files')
      .insert({
        project_id: projectId,
        file_name: fileName,
        file_url: url,
        file_type: 'text/plain',
      })
      .select()
      .single();

    if (dbError) {
      const path = getFilePathFromUrl(url);
      if (path) await deleteFile(path);
      setError('Not kaydedilirken bir hata oluştu');
    } else if (inserted) {
      setFiles((prev) => [inserted, ...prev]);
      setShowAddNote(false);
      setNoteData({ title: '', content: '' });
    }
    
    setAddingNote(false);
  };

  const handleOpenNote = async (file: StoredFile) => {
    setEditingNote(file);
    setShowEditNote(true);
    setIsNoteLoading(true);
    try {
      const response = await fetch(file.file_url);
      if (!response.ok) throw new Error('Not okunamadı');
      const text = await response.text();
      setEditNoteContent(text);
    } catch (err) {
      console.error(err);
      setError('Not içeriği yüklenirken hata oluştu');
    } finally {
      setIsNoteLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!editingNote) return;
    setIsNoteSaving(true);
    setError('');

    try {
      const blob = new Blob([editNoteContent], { type: 'text/plain;charset=utf-8' });
      const fileObj = new File([blob], editingNote.file_name, { type: 'text/plain' });

      // Upload new file
      const { url, error: uploadError } = await uploadFile(fileObj, projectId, 'files');
      if (uploadError || !url) throw new Error(uploadError?.message || 'Yükleme hatası');

      // Update DB record
      const { error: dbError } = await supabase
        .from('files')
        .update({ file_url: url })
        .eq('id', editingNote.id);

      if (dbError) throw new Error('Veritabanı güncellenemedi');

      // Delete old file from storage to save space
      const oldPath = editingNote.storage_path || getFilePathFromUrl(editingNote.file_url);
      if (oldPath) await deleteFile(oldPath);

      // Update local state
      setFiles(prev => prev.map(f => f.id === editingNote.id ? { ...f, file_url: url } : f));
      setShowEditNote(false);
      setEditingNote(null);
    } catch (err: any) {
      setError(err.message || 'Not kaydedilirken bir hata oluştu');
    } finally {
      setIsNoteSaving(false);
    }
  };

  const handleCopyNote = async () => {
    try {
      await navigator.clipboard.writeText(editNoteContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Kopyalama hatası:', err);
    }
  };

  const handleDelete = async (file: StoredFile) => {
    setError('');

    // ✅ FIX 3: Önce veritabanından sil
    const { error: dbError } = await supabase
      .from('files')
      .delete()
      .eq('id', file.id);

    if (dbError) {
      setError('Dosya silinirken bir hata oluştu');
      return;
    }

    // Sonra storage'dan sil (hata olsa da state güncellenir)
    if (file.file_type !== 'url') {
        const path = file.storage_path || getFilePathFromUrl(file.file_url);
        if (path) {
            const { error: storageError } = await deleteFile(path);
            if (storageError) {
                // Storage hatası kullanıcıya bildirilir ama dosya listeden kaldırılır
                setError('Dosya kaydı silindi ancak storage temizlenirken hata oluştu.');
            }
        }
    }

    setFiles((prev) => prev.filter((f) => f.id !== file.id));
  };

  const getFileIcon = (type: string) => fileTypeIcons[type] || 'ri-file-line';
  const getFileColor = (type: string) => fileTypeColors[type] || 'text-stone-500 bg-stone-100';
  const getFileLabel = (type: string) => fileTypeLabels[type] || 'Dosya';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Dosyalar</h2>
          <p className="text-xs text-stone-400 mt-0.5">{files.length} dosya</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowAddNote(true); setShowAddLink(false); setShowUpload(false); }}
            className="px-3 py-2 bg-white border border-stone-200 text-stone-600 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-sticky-note-line"></i>
            Not Ekle
          </button>
          <button
            onClick={() => { setShowAddLink(true); setShowAddNote(false); setShowUpload(false); }}
            className="px-3 py-2 bg-white border border-stone-200 text-stone-600 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-link"></i>
            Bağlantı Ekle
          </button>
          <button
            onClick={() => { setShowUpload(true); setShowAddLink(false); setShowAddNote(false); }}
            disabled={uploading}
            className="px-3 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            <i className="ri-upload-cloud-line"></i>
            {uploading ? 'Yükleniyor...' : 'Dosya Yükle'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Upload Area */}
      {showUpload && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragOver ? 'border-amber-500 bg-amber-50' : 'border-stone-300 bg-white'
          }`}
        >
          <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
            <i className="ri-upload-cloud-2-line text-stone-400 text-xl"></i>
          </div>
          <p className="text-sm text-stone-600 font-medium">Dosyaları buraya sürükleyin</p>
          <p className="text-xs text-stone-400 mt-1">veya</p>
          <label className="mt-3 inline-block px-4 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors cursor-pointer whitespace-nowrap">
            Dosya Seç
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowUpload(false)}
            className="block mx-auto mt-3 text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
          >
            İptal
          </button>
        </div>
      )}

      {/* Add Link Form */}
      {showAddLink && (
        <form onSubmit={handleAddLink} className="bg-white rounded-xl p-5 border border-stone-200 shadow-sm space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-link text-amber-600"></i>
            <h3 className="text-sm font-bold text-stone-800">Harici Bağlantı Ekle</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Bağlantı Adı</label>
              <input
                type="text"
                value={linkData.name}
                onChange={(e) => setLinkData({ ...linkData, name: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                placeholder="Örn: Google Drive Klasörü"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">URL</label>
              <input
                type="text"
                value={linkData.url}
                onChange={(e) => setLinkData({ ...linkData, url: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={() => setShowAddLink(false)}
              className="px-4 py-2 text-stone-600 text-sm font-medium hover:text-stone-800 transition-colors cursor-pointer"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={addingLink}
              className="px-6 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors disabled:opacity-50 cursor-pointer"
            >
              {addingLink ? 'Ekleniyor...' : 'Bağlantıyı Ekle'}
            </button>
          </div>
        </form>
      )}

      {/* Add Note Form */}
      {showAddNote && (
        <form onSubmit={handleAddNote} className="bg-white rounded-xl p-5 border border-stone-200 shadow-sm space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-sticky-note-line text-amber-600"></i>
            <h3 className="text-sm font-bold text-stone-800">Not Ekle</h3>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Not Başlığı</label>
            <input
              type="text"
              value={noteData.title}
              onChange={(e) => setNoteData({ ...noteData, title: e.target.value })}
              required
              className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
              placeholder="Örn: Toplantı Notları"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">İçerik (Maksimum 2000 Kelime)</label>
            <textarea
              value={noteData.content}
              onChange={(e) => {
                const text = e.target.value;
                const wordsCount = text.trim().split(/\s+/).filter(w => w.length > 0).length;
                if (wordsCount <= 2000) {
                  setNoteData({ ...noteData, content: text });
                } else {
                  // Keep only up to 2000 words
                  const words = text.split(/\s+/).slice(0, 2000);
                  setNoteData({ ...noteData, content: words.join(' ') });
                }
              }}
              required
              rows={5}
              className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all resize-none"
              placeholder="Notunuzu buraya yazın..."
            />
            <div className="text-right mt-1 text-xs text-stone-400">
              {noteData.content.trim() ? noteData.content.trim().split(/\s+/).filter(w => w.length > 0).length : 0} / 2000 kelime
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={() => setShowAddNote(false)}
              className="px-4 py-2 text-stone-600 text-sm font-medium hover:text-stone-800 transition-colors cursor-pointer"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={addingNote}
              className="px-6 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors disabled:opacity-50 cursor-pointer"
            >
              {addingNote ? 'Kaydediliyor...' : 'Notu Kaydet'}
            </button>
          </div>
        </form>
      )}

      {/* Files Grid */}
      {files.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
            <i className="ri-folder-line text-stone-400 text-2xl"></i>
          </div>
          <p className="text-stone-400 text-sm">Henüz dosya bulunmuyor</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="bg-white rounded-xl p-4 border border-stone-200 hover:border-stone-300 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${getFileColor(file.file_type)}`}>
                  <i className={`${getFileIcon(file.file_type)} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  {file.file_type === 'text/plain' ? (
                    <button
                      onClick={() => handleOpenNote(file)}
                      className="text-sm font-medium text-stone-800 truncate hover:text-amber-600 transition-colors block text-left cursor-pointer"
                      title={file.file_name}
                    >
                      {file.file_name}
                    </button>
                  ) : (
                    <a
                      href={file.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-stone-800 truncate hover:text-amber-600 transition-colors block"
                      title={file.file_name}
                    >
                      {file.file_name}
                    </a>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-stone-400">{getFileLabel(file.file_type)}</span>
                    <span className="text-xs text-stone-300">•</span>
                    <span className="text-xs text-stone-400">
                      {new Date(file.created_at).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(file)}
                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-stone-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Note Modal */}
      {showEditNote && editingNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl flex flex-col overflow-hidden" style={{ height: '80vh', minHeight: '600px' }}>
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg text-amber-500">
                  <i className="ri-sticky-note-line text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-stone-800 text-lg">{editingNote.file_name.replace('.txt', '')}</h3>
                  <p className="text-xs text-stone-500">Notu Düzenle</p>
                </div>
              </div>
              <button
                onClick={() => { setShowEditNote(false); setEditingNote(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-0 flex-1 flex flex-col relative bg-white">
              {isNoteLoading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
                </div>
              ) : (
                <div className="flex-1 flex flex-col p-6">
                  <textarea
                    value={editNoteContent}
                    onChange={(e) => {
                      const text = e.target.value;
                      const wordsCount = text.trim().split(/\s+/).filter(w => w.length > 0).length;
                      if (wordsCount <= 2000) {
                        setEditNoteContent(text);
                      } else {
                        const words = text.split(/\s+/).slice(0, 2000);
                        setEditNoteContent(words.join(' '));
                      }
                    }}
                    className="flex-1 w-full p-6 rounded-xl border border-stone-200 text-stone-800 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none bg-stone-50/50"
                    placeholder="Notunuzu yazın..."
                  />
                  <div className="text-right mt-3 text-sm text-stone-400 font-medium">
                    {editNoteContent.trim() ? editNoteContent.trim().split(/\s+/).filter(w => w.length > 0).length : 0} / 2000 kelime
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-stone-200 flex justify-between items-center bg-stone-50">
              <button
                onClick={handleCopyNote}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 cursor-pointer ${
                  isCopied ? 'bg-emerald-50 text-emerald-600' : 'text-stone-600 hover:bg-stone-200'
                }`}
                title="Metni Kopyala"
              >
                <i className={isCopied ? "ri-check-line" : "ri-clipboard-line"}></i>
                {isCopied ? 'Kopyalandı!' : 'Kopyala'}
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowEditNote(false); setEditingNote(null); setIsCopied(false); }}
                  className="px-5 py-2.5 text-stone-600 text-sm font-medium hover:text-stone-800 transition-colors cursor-pointer"
                >
                  İptal
                </button>
                <button
                  onClick={handleSaveNote}
                  disabled={isNoteSaving || isNoteLoading}
                  className="px-6 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 shadow-sm cursor-pointer"
                >
                  {isNoteSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
